import { atom } from 'jotai';

import type { WorkspaceTasksResponse } from 'types/type-curve-workspace-show';
import { TASK_CATALOG } from './task-catalog';
import {
  createTaskInstance,
  type TaskInstance,
  type TaskStatus,
} from './task-types';

// ─── Backend-driven verification tasks (GET /type_curve_workspaces/:id/tasks) ───
// Read mirror of the tasks query cache (written by useWorkspaceTasksQuery). The
// panel and header badge read these; the local catalog atoms above remain for
// the per-field verify icons / area-selector / promote modal until those migrate.

const EMPTY_TASKS: WorkspaceTasksResponse = {
  tasks: [],
  completed: 0,
  total: 0,
};

export const tcWorkspaceTasksAtom = atom<WorkspaceTasksResponse>(EMPTY_TASKS);

// The "overall Type Curve" verification. It is NOT a checklist row: it's driven
// by the header "Mark as Correct/Incorrect" buttons (patched via its task path)
// and is excluded from the list, the counts, and the promote gate.
export const OVERALL_TYPE_CURVE_KEY = 'type_curve';

// Counts exclude the overall Type Curve task (it lives on the header, not the
// checklist), so they're recomputed from the tasks list rather than trusting the
// backend `.total`/`.completed`, which include it.
export const tcWorkspaceTasksTotalAtom = atom(
  (get) =>
    get(tcWorkspaceTasksAtom).tasks.filter(
      (t) => t.group !== OVERALL_TYPE_CURVE_KEY
    ).length
);

export const tcWorkspaceTasksCompletedAtom = atom(
  (get) =>
    get(tcWorkspaceTasksAtom).tasks.filter(
      (t) => t.group !== OVERALL_TYPE_CURVE_KEY && t.status !== null
    ).length
);

// Derived: all REQUIRED tasks carry a verdict (status !== null). Drives the
// header Promote button. Excludes the overall Type Curve task (set separately
// via the header). False when there are no tasks / no required tasks.
export const tcRequiredTasksCompleteAtom = atom((get) => {
  const required = get(tcWorkspaceTasksAtom).tasks.filter(
    (t) => t.required && t.group !== OVERALL_TYPE_CURVE_KEY
  );
  return required.length > 0 && required.every((t) => t.status !== null);
});

const VOLUMES_GROUP = 'type_curve_volumes';

// Derived: at least one Type Curve Volumes task (oil/gas/water) carries a
// verdict. Mirrors the backend ANY_OF_GROUPS semantics — a type curve doesn't
// need all three streams fitted, but promotion requires at least one confirmed.
export const tcVolumesVerdictExistsAtom = atom((get) => {
  const volumesTasks = get(tcWorkspaceTasksAtom).tasks.filter(
    (t) => t.group === VOLUMES_GROUP
  );
  return volumesTasks.length > 0 && volumesTasks.some((t) => t.status !== null);
});

// Derived: the overall Type Curve itself has been marked Correct or Incorrect
// from the header. It is the final gate and is deliberately excluded from
// tcRequiredTasksCompleteAtom above, so promotion has to check it separately.
export const tcOverallVerdictSetAtom = atom((get) =>
  get(tcWorkspaceTasksAtom).tasks.some(
    (t) => t.group === OVERALL_TYPE_CURVE_KEY && t.status !== null
  )
);

// A task `path` to auto-expand in the panel (set by the per-field verify icon;
// the panel consumes and clears it).
export const tcExpandTaskPathAtom = atom<string | null>(null);

/** All task instances for the active workspace. */
export const tcTasksAtom = atom<TaskInstance[]>([]);

/** Staged verdicts that haven't been committed yet (for tasks requiring note or filter context). */
export const tcPendingVerdictsAtom = atom<Record<string, TaskStatus>>({});

/** Task ID to auto-expand in the panel when opened. */
export const tcTaskPanelInitialExpandAtom = atom<string | null>(null);

/** Derived: total task count. */
export const tcTasksTotalAtom = atom((get) => get(tcTasksAtom).length);

/** Derived: count of tasks with a non-unverified status. */
export const tcTasksCompletedAtom = atom(
  (get) => get(tcTasksAtom).filter((t) => t.status !== 'unverified').length
);

/**
 * Write atom: initializes the 16 default catalog tasks and merges backend
 * verification statuses for the two backend-backed tasks.
 */
export const initializeTasksAtom = atom(
  null,
  (
    _get,
    set,
    payload: {
      unitResolutionStatus: string | null;
      inventoryVerificationStatus: string | null;
    }
  ) => {
    const tasks = TASK_CATALOG.map((entry) => {
      const instance = createTaskInstance(entry.id);

      // Merge backend status for resolve-unit-overlaps
      if (
        entry.id === 'resolve-unit-overlaps' &&
        payload.unitResolutionStatus
      ) {
        instance.status =
          payload.unitResolutionStatus === 'incorrect'
            ? 'incorrect'
            : 'correct';
      }

      // Merge backend status for verify-inventory
      if (
        entry.id === 'verify-inventory' &&
        payload.inventoryVerificationStatus
      ) {
        instance.status =
          payload.inventoryVerificationStatus === 'incorrect'
            ? 'incorrect'
            : 'correct';
      }

      return instance;
    });

    set(tcTasksAtom, tasks);
    set(tcPendingVerdictsAtom, {});
  }
);

/** Write atom: append an action task to the list. */
export const addActionTaskAtom = atom(null, (_get, set, task: TaskInstance) => {
  set(tcTasksAtom, (prev) => [...prev, task]);
});

/** Write atom: remove an action task by ID. */
export const removeActionTaskAtom = atom(null, (_get, set, taskId: string) => {
  set(tcTasksAtom, (prev) => prev.filter((t) => t.id !== taskId));
});
